<template>
    <div class="container mt-5">
        <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading instance details...</p>
        </div>
        <form v-else @submit.prevent="saveInstance">
            <!-- ID -->
            <div class="row mb-3">
                <div class="col-md-6">
                    <MaterialInput id="instance-id" label="ID" v-model="instanceData.id" disabled />
                </div>
                <div class="col-md-6">
                    <MaterialInput id="instance-name" label="Name" v-model="instanceData.name" readOnly />
                </div>
            </div>

            <!-- Description -->
            <div class="mb-3">
                <MaterialInput id="instance-description" label="Description" v-model="instanceData.description" readOnly />
            </div>

            <!-- Start Date & Time -->
                <div class="row mb-3">
                    <div class="col-md-6">
                        <MaterialDatePicker id="instance-start-date" label="Start Date" v-model="instanceData.startDate" :disabled="readOnly" />
                    </div>
                    <div class="col-md-6">
                        <MaterialInput id="instance-start-time" type="time" label="Start Time" v-model="instanceData.startTime" :readOnly="readOnly" />
                    </div>
                </div>

            <!-- End Date & Time -->
            <div class="row mb-3">
                <div class="col-md-6">
                    <MaterialDatePicker id="instance-end-date" label="End Date" v-model="instanceData.endDate" :disabled="readOnly" />
                </div>
                <div class="col-md-6">
                    <MaterialInput id="instance-end-time" type="time" label="End Time" v-model="instanceData.endTime" :readOnly="readOnly" />
                </div>
            </div>

            <!-- Status -->
            <div class="row mb-3">
                <div class="col-md-6">
                    <MaterialSelect label="Status" v-model="instanceData.status" :options="statusOptions" :disabled="readOnly" />
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Completed Auto Schedule</label>
                        <HelpTextTooltip helpKey="activityinstance_detail_schedcomplete" />
                    </div>
                    <MaterialSelect v-model="instanceData.completedAutoSchedule"
                        :options="yesNoOptions" :disabled="readOnly" />
                </div>
            </div>

            <!-- Last Schedule Date -->
            <div class="row mb-3">
                <div class="col-md-6">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Last Schedule Date</label>
                        <HelpTextTooltip helpKey="activityinstance_detail_lastscheddate" />
                    </div>
                    <MaterialInput id="instance-last-schedule-date" v-model="instanceData.lastScheduleDate" disabled />
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Last Admin Report Date</label>
                        <HelpTextTooltip helpKey="activityinstance_detail_lastreportdate" />
                    </div>
                    <MaterialInput id="instance-last-admin-report-date" v-model="instanceData.lastAdminReportDate" disabled />
                </div>
            </div>

            <!-- Notes -->
            <div class="row mb-3">
                <MaterialTextarea label="Notes" v-model="instanceData.notes" :readOnly="readOnly" />
            </div>

            <!-- Buttons -->
            <div v-if="!readOnly" class="d-flex justify-content-end mt-4">
                <MaterialButton @click="saveInstance" variant="primary" :class="['px-4', hasChanges && !isSaving ? 'pulse-glow' : '']" :disabled="isSaving || isLoading">
                    <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <i v-else class="material-icons text-lg me-1">save</i>
                    {{ isSaving ? 'Saving...' : 'Save' }}
                </MaterialButton>
            </div>
        </form>
    </div>
</template>

<script setup>
import { reactive, ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialInput from '@/components/common/MaterialInput.vue'
import MaterialTextarea from '@/components/common/MaterialTextarea.vue'
import MaterialSelect from '@/components/common/MaterialSelect.vue'
import MaterialButton from '@/components/common/MaterialButton.vue'
import MaterialDatePicker from '@/components/common/MaterialDatePicker.vue'
import HelpTextTooltip from '@/components/common/HelpTextTooltip.vue'

defineProps({
    readOnly: { type: Boolean, default: false }
})

const store = useStore()
const route = useRoute()
const toast = useToast()

// Get IDs from route params
const locationId = route.params.locationid
const activityInstanceId = route.params.activityinstanceid

const instanceData = reactive({
    id: '',
    name: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    status: 'active',
    completedAutoSchedule: 'no',
    lastScheduleDate: '',
    lastAdminReportDate: '',
    notes: ''
})

const isSaving = ref(false)
const isLoading = computed(() => store.getters['Instance/instanceLoading'])
const baseline = ref(null)

const hasChanges = computed(() => {
    if (!baseline.value) return false
    return JSON.stringify(baseline.value) !== JSON.stringify({ ...instanceData })
})

const yesNoOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
]

const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Pending', value: 'pending' },
    { label: 'Completed', value: 'completed' }
]

// Format date from ISO string to YYYY-MM-DD
const formatDateForInput = (isoString) => {
    if (!isoString) return ''
    const date = new Date(isoString)
    return date.toISOString().split('T')[0]
}

// Format time from ISO string to HH:MM
const formatTimeForInput = (isoString) => {
    if (!isoString) return ''
    const date = new Date(isoString)
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
}

// Format date for API (MM/DD/YYYY)
const formatDateForAPI = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}/${day}/${year}`
}

// Format time for API (HH:MM AM/PM)
const formatTimeForAPI = (timeString) => {
    if (!timeString) return ''
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour % 12 || 12
    return `${hour12}:${minutes} ${ampm}`
}

// Load instance detail
const loadInstanceDetail = async () => {
    try {
        const detail = await store.dispatch('getActivityInstanceDetail', {
            locationId,
            activityinstanceid: activityInstanceId
        })

        if (detail) {
            instanceData.id = detail.id || ''
            instanceData.name = detail.activityname || '' // May not be in response
            instanceData.description = detail.description || '' // May not be in response
            instanceData.startDate = formatDateForInput(detail.startdate)
            instanceData.startTime = formatTimeForInput(detail.startdate)
            instanceData.endDate = formatDateForInput(detail.enddate)
            instanceData.endTime = formatTimeForInput(detail.enddate)
            instanceData.status = detail.status || 'active'
            instanceData.completedAutoSchedule = detail.completedautoschedule ? 'yes' : 'no'
            instanceData.lastScheduleDate = detail.last_autoschedule_date_display || ''
            instanceData.lastAdminReportDate = detail.getlastadminalertdate_display || ''
            instanceData.notes = detail.note || ''
            baseline.value = { ...instanceData }
        }
    } catch (error) {
        console.error('Error loading instance detail:', error)
        toast.error('Failed to load instance details', { position: 'top-right' })
    }
}

const saveInstance = async () => {
    isSaving.value = true
    try {
        await store.dispatch('saveInstanceDetail', {
            locationId,
            instanceId: activityInstanceId,
            startDate: formatDateForAPI(instanceData.startDate),
            startTime: formatTimeForAPI(instanceData.startTime),
            endDate: formatDateForAPI(instanceData.endDate),
            endTime: formatTimeForAPI(instanceData.endTime),
            status: instanceData.status,
            autoScheduled: instanceData.completedAutoSchedule === 'yes',
            note: instanceData.notes
        })

        baseline.value = { ...instanceData }
        toast.success('Instance details saved successfully', { position: 'top-right' })
        
        // Reload the data to get updated values
        await loadInstanceDetail()
    } catch (error) {
        console.error('Error saving instance:', error)
        toast.error('Failed to save instance details', { position: 'top-right' })
    } finally {
        isSaving.value = false
    }
}

// Load data on mount
onMounted(() => {
    loadInstanceDetail()
})
</script>

<style scoped>
.form-label {
    color: #7b809a;
}

.d-flex {
    display: flex;
}

.align-items-center {
    align-items: center;
}

.gap-2 {
    gap: 0.5rem;
}
</style>
